import { FunctionComponent, MouseEventHandler, ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { ACCENT } from "@/theme/tokens";
import { classNames } from "@/utils/classNames";

import { Spinner } from "../Spinner/Spinner";
import { ButtonColor, ButtonSize, ButtonStatus, FontSize } from "./types";

interface ButtonProps {
	title: React.ReactNode;
	onClick?: MouseEventHandler;
	type?: "submit" | "reset" | "button";
	disabled?: boolean;
	disabledInGray?: boolean;
	status?: ButtonStatus;
	color?: ButtonColor;
	size?: ButtonSize;
	fontSize?: FontSize;
	isLoading?: boolean;
	image?: ReactElement;
	className?: string;
	testId?: string;
	iconRight?: boolean;
}

export const Button: FunctionComponent<ButtonProps> = ({
	title,
	onClick,
	type = "submit",
	status,
	disabled,
	disabledInGray,
	isLoading,
	color = ButtonColor.BRAND,
	fontSize,
	size = ButtonSize.M,
	image,
	className,
	testId,
	iconRight,
}) => {
	const { t } = useTranslation();

	// The two tall sizes are the primary-CTA sizes, so they default to a 16px label
	// rather than the 14px everything else uses. An explicit `fontSize` still wins.
	const isTallSize = size === ButtonSize.L || size === ButtonSize.ML;
	const effectiveFontSize = fontSize ?? (isTallSize ? FontSize.M : FontSize.SM);

	/**
	 * White on `accent` is 3.12:1 — it carries a bold ≥16px label and nothing smaller
	 * (guide §4.1, §6.2). Below that the primary button is `ink`, which is 18.9:1 at
	 * any size. This is why there is no such thing as an extra-small orange button.
	 */
	const primaryFill =
		effectiveFontSize === FontSize.M
			? "bg-accent hover:bg-accent-dark text-white border-transparent"
			: "bg-ink hover:bg-neutral-700 text-white border-transparent";

	const getStatusClasses = () => {
		switch (status) {
			// Ghost buttons: the border may be `accent` (3.12:1 clears the 3:1 WCAG asks
			// of a meaningful border) but the label may not — that is accent-strong.
			case ButtonStatus.SUCCESS:
				return "!border-accent !bg-transparent !text-accent-strong";
			case ButtonStatus.ACTION:
				return "!border-accent-strong !bg-white !text-accent-strong";
		}
	};

	const getColorClasses = () => {
		switch (color) {
			case ButtonColor.BRAND:
			case ButtonColor.ACTION:
				return primaryFill;
			case ButtonColor.PRIMARY:
				return "bg-success text-white border-transparent";
			case ButtonColor.ACTION_SECONDARY:
				return classNames(
					"bg-white border border-border text-text hover:border-accent",
					disabledInGray ? "disabled:!border-grey-soft disabled:!bg-transparent" : ""
				);
			case ButtonColor.DANGER:
			case ButtonColor.ERROR:
				return "bg-danger border-danger text-white";
			case ButtonColor.DANGER_SECONDARY:
				return "bg-white border-danger text-danger";
			// Amber cannot carry white: 2.27:1. Ink on amber is the only legible pair.
			case ButtonColor.WARNING:
				return "bg-warning text-ink border-transparent";
			case ButtonColor.SUCCESS:
				return "bg-success text-white";
			case ButtonColor.DISABLED:
				return "bg-border-soft text-muted border-transparent";
			case ButtonColor.DECISION:
				return "border border-border bg-success text-white rounded-button";
		}
	};

	const getSizeClasses = () => {
		switch (size) {
			case ButtonSize.XXS:
				return "h-[24px] w-[24px] px-1 text-xs";
			case ButtonSize.XS:
				return "h-[28px] px-2 text-xs";
			case ButtonSize.S:
				return "h-[32px] px-4 text-xs";
			case ButtonSize.M:
				return "h-[38px] px-4";
			case ButtonSize.ML:
				return "h-[42px] px-4";
			case ButtonSize.L:
				return "h-[48px] px-6";
		}
	};

	const getFontSizeClasses = () => {
		switch (effectiveFontSize) {
			case FontSize.XS:
				return "text-xs";
			case FontSize.SM:
				return "text-sm";
			case FontSize.M:
				return "text-[16px]";
			default:
				return "";
		}
	};

	return (
		<button
			className={classNames(
				// 6px radius, bold label (guide §3, §4.1). The weight is load-bearing on
				// the accent fill, not decoration.
				"appearance-none text-center font-bold rounded-button w-full border duration-200 shadow-sm",
				getColorClasses(),
				status ? getStatusClasses() : "",
				getSizeClasses(),
				getFontSizeClasses(),
				disabled ? "opacity-60" : "",
				disabledInGray ? "disabled:bg-border-soft disabled:text-grey-light" : "",
				className || ""
			)}
			data-clicktarget="tableaction"
			data-test={testId}
			disabled={isLoading || disabled}
			type={type}
			onClick={onClick}
		>
			{isLoading ? (
				<div className="flex items-center justify-center">
					<Spinner color={color === ButtonColor.ACTION_SECONDARY ? ACCENT.accentStrong : ""} />{" "}
					<div className="ml-3 whitespace-nowrap">{t("basics.wait")}</div>
				</div>
			) : (
				<div className="flex items-center justify-center max-w-full text-center whitespace-nowrap text-ellipsis">
					{iconRight ? (
						<>
							{title}
							{image}
						</>
					) : (
						<>
							{image}
							{/* Inherits the button's font size — it used to hardcode text-sm, which
							    silently capped the CTA at 14px and broke its contrast promise. */}
							<div className="leading-[16px] h-full mt-0.5">{title}</div>
						</>
					)}
				</div>
			)}
		</button>
	);
};
